<template>
  <section class="text-hero-module-component">
    <div
      class="hero-top"
      :class="{ 'white-version': data.use_white_version }">
      <div class="inner-container">
        <div class="hero-text">
          <div
            class="hero-title"
            v-html="splitText(props.data.hero_text)" />

          <div
            v-if="data.add_hero_subtitle"
            class="subtitle"
            v-html="data.hero_subtitle"></div>

          <div class="bicolor-line-container">
            <BicolorLine />
          </div>
        </div>
      </div>
    </div>
  </section>

  <template v-if="data.add_spacer">
    <SpacerComponent :data="negativeSpacerData" />
    <SpacerComponent
      class="text-hero-positive-spacer"
      :data="positiveSpacerData" />
  </template>
</template>

<script setup>
/* #META
   {
    "NAME": "TextHeroComponent",
    "CLASSNAME": "text-hero-module-component",
    "URL": "http://vuevitewp.local/test"
   }
  #ENDMETA */
import { computed, onMounted } from 'vue'
import BicolorLine from '@/components/utility/BicolorLine.vue'
import SpacerComponent from '@/components/modules/SpacerComponent.vue'
import useUtils from '@/components/composables/useUtils.js'
import useIntroShown from '@/components/composables/useIntroShown.js'
import useHeroShared from '@/components/composables/useHeroShared.js'

const { heroAnimationComplete } = useUtils()
const { introShown } = useIntroShown()
const { splitHeroText, runTextHeroIntroAnimation } = useHeroShared()
const props = defineProps(['data'])

const spacerSettings = computed(() => props.data?.spacer || {})

const spacerValue = (snakeCaseKey, camelCaseKey) => {
  const value =
    spacerSettings.value?.[snakeCaseKey] ?? spacerSettings.value?.[camelCaseKey]
  const number = Number.parseFloat(value)

  return Number.isFinite(number) ? Math.abs(number) : 0
}

const spacerHeights = computed(() => ({
  desktop: spacerValue('desktop_spacer', 'desktopSpacer'),
  tablet: spacerValue('tablet_spacer', 'tabletSpacer'),
  mobile: spacerValue('mobile_spacer', 'mobileSpacer'),
}))

const negativeSpacerData = computed(() => ({
  separator: false,
  rounded: 'none',
  spacer_height: -spacerHeights.value.desktop,
  spacer_height_tablet: -spacerHeights.value.tablet,
  spacer_height_mobile: -spacerHeights.value.mobile,
  back_background_color: 'rgba(255, 255, 255, 0)',
  background_color: 'rgba(255, 255, 255, 0)',
}))

const positiveSpacerData = computed(() => ({
  separator: false,
  rounded:
    spacerSettings.value?.rounded_corner ??
    spacerSettings.value?.roundedCorner ??
    'none',
  spacer_height: spacerHeights.value.desktop,
  spacer_height_tablet: spacerHeights.value.tablet,
  spacer_height_mobile: spacerHeights.value.mobile,
  back_background_color: 'rgba(255, 255, 255, 0)',
  background_color:
    spacerSettings.value?.background_color ??
    spacerSettings.value?.backgroundColor ??
    'rgba(255, 255, 255, 0)',
}))

const splitText = splitHeroText

onMounted(() => {
  runTextHeroIntroAnimation({
    rootSelector: '.text-hero-module-component',
    introShown,
    heroAnimationComplete,
    hasSubtitle: props.data.add_hero_subtitle,
    hasBicolor: true,
  })
})
</script>

<style lang="scss" scoped>
.text-hero-module-component {
  @apply block relative z-0;

  .hero-top {
    @apply min-h-[394px] flex flex-col justify-start bg-humble relative pt-[150px] pb-[80px]
    md:pt-[210px] md:min-h-[454px] lg:min-h-[561px] z-1;

    .hero-text {
      @apply relative
      md:max-w-[490px]
      lg:pb-10 lg:max-w-[880px];

      .hero-title {
        &:deep(h1) {
          @apply font-FKDisplayALT text-[40px] leading-[1.2] tracking-[-0.8px]
          md:text-6xl md:not-italic md:font-normal md:leading-[60px] md:tracking-[-1.2px];
          br {
            @apply hidden lg:block;
          }
          strong {
            @apply font-normal text-progressive;
          }
          span {
            @apply text-white overflow-hidden block h-[66px] lg:h-[72px];
            span {
              @apply inline-block h-[66px] lg:h-[72px];
              strong {
                @apply font-normal text-progressive;
              }
            }
          }
        }
      }

      .subtitle {
        @apply pt-5;

        :deep(p) {
          @apply text-[16px] leading-tight text-white lg:text-[22px];
        }
      }
      .bicolor-line-container {
        @apply relative overflow-hidden w-full;
      }
      .bicolor-line {
        @apply mt-6 h-[4px] w-[120px]
        lg:mt-6 lg:h-[5px] lg:w-[220px];
      }
    }

    &.white-version {
      @apply bg-white;

      .hero-text {
        .hero-title {
          &:deep(h1) {
            span {
              @apply text-balanced;
            }
          }
        }
        .subtitle {
          :deep(p) {
            @apply text-balanced;
          }
        }
      }
    }
  }
}

.text-hero-positive-spacer {
  @apply z-2;
}
</style>
